import { useState, useEffect, useCallback } from 'react'
import { ShieldCheck, Plus, Download, RefreshCw, X, Save, Settings2, FileText, AlertTriangle } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { frDate } from '../lib/qseRules'

interface Item {
  id: string; categorie: string; libelle: string; employeeId: string | null; employeeName: string | null
  dernierControle: string | null; periodiciteMois: number | null; prochaineEcheance: string | null
  prestataire: string | null; documentUrl: string | null; notes: string | null
  etat: 'ok' | 'bientot' | 'depasse' | 'inconnu'
}
interface Summary { echeancesDepassees: number; echeancesProches: number; temperaturesHorsLimite7j: number; livraisonsNonConformes7j: number }
interface PeriodeLivraison { du?: string; au?: string; jours: number[] }
interface Settings { responsableEmail: string | null; seuilFrais: number; seuilSurgele: number; ecartMaxSonde: number; fournisseurs: string[]; livraisons?: PeriodeLivraison[]; lieux?: string[] }
const JOURS_COURTS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

// Jours de livraison par saison : la 1re période qui couvre la date gagne,
// la ligne « reste de l'année » (sans dates) sert pour tout le reste.
function LivraisonsEditor({ value, onChange, canEdit }: { value: PeriodeLivraison[]; onChange: (v: PeriodeLivraison[]) => void; canEdit: boolean }) {
  const estSaison = (p: PeriodeLivraison) => p.du !== undefined || p.au !== undefined
  const saisons = value.filter(estSaison)
  const reste = value.find(p => !estSaison(p)) || { jours: [] }
  const set = (saisonsN: PeriodeLivraison[], resteN: PeriodeLivraison) => onChange([...saisonsN, { jours: resteN.jours }])
  const toggle = (jours: number[], j: number) => (jours.includes(j) ? jours.filter(x => x !== j) : [...jours, j].sort())
  const Jours = ({ jours, onToggle }: { jours: number[]; onToggle: (j: number) => void }) => (
    <div className="flex gap-1">
      {JOURS_COURTS.map((l, j) => (
        <button key={j} type="button" disabled={!canEdit} onClick={() => onToggle(j)}
          className={`w-10 py-1 rounded text-xs font-medium border ${jours.includes(j) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>{l}</button>
      ))}
    </div>
  )
  return (
    <div className="space-y-2">
      {saisons.map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">du</span>
          <input className="border rounded px-2 py-1 text-sm w-20" placeholder="06-01" disabled={!canEdit} value={p.du}
            onChange={e => set(saisons.map((x, k) => (k === i ? { ...x, du: e.target.value } : x)), reste)} />
          <span className="text-xs text-gray-500">au</span>
          <input className="border rounded px-2 py-1 text-sm w-20" placeholder="08-31" disabled={!canEdit} value={p.au}
            onChange={e => set(saisons.map((x, k) => (k === i ? { ...x, au: e.target.value } : x)), reste)} />
          <Jours jours={p.jours} onToggle={j => set(saisons.map((x, k) => (k === i ? { ...x, jours: toggle(x.jours, j) } : x)), reste)} />
          {canEdit && <button type="button" className="text-xs text-red-600" onClick={() => set(saisons.filter((_, k) => k !== i), reste)}>Retirer</button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 w-40">Reste de l'année</span>
        <Jours jours={reste.jours} onToggle={j => set(saisons, { jours: toggle(reste.jours, j) })} />
      </div>
      {canEdit && (
        <button type="button" className="text-xs text-blue-600" onClick={() => set([...saisons, { du: '', au: '', jours: [] }], reste)}>
          + Ajouter une saison (dates au format MM-JJ)
        </button>
      )}
    </div>
  )
}

export const CATEGORIES: { value: string; label: string }[] = [
  { value: 'electricite', label: 'Électricité' },
  { value: 'incendie', label: 'Incendie / extincteurs' },
  { value: 'portes', label: 'Portes automatiques' },
  { value: 'froid', label: 'Froid / fluides' },
  { value: 'duerp', label: 'DUERP' },
  { value: 'balances', label: 'Balances (métrologie)' },
  { value: 'audit', label: 'Audit' },
  { value: 'videoprotection', label: 'Vidéoprotection' },
  { value: 'formation', label: 'Formation' },
  { value: 'medical', label: 'Visite médicale' },
  { value: 'autre', label: 'Autre' },
]
const catLabel = (c: string) => CATEGORIES.find(x => x.value === c)?.label || c

const ETAT: Record<Item['etat'], { label: string; row: string; pill: string }> = {
  depasse: { label: 'Dépassé', row: 'bg-red-50', pill: 'bg-red-600 text-white' },
  bientot: { label: 'Sous 30 jours', row: 'bg-amber-50', pill: 'bg-amber-500 text-white' },
  ok: { label: 'À jour', row: '', pill: 'bg-green-600 text-white' },
  inconnu: { label: 'Date inconnue', row: 'bg-gray-50', pill: 'bg-gray-400 text-white' },
}

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'

async function download(url: string, filename: string) {
  const r = await api.get(url, { responseType: 'blob' })
  const href = URL.createObjectURL(r.data)
  const a = document.createElement('a')
  a.href = href; a.download = filename; a.click()
  URL.revokeObjectURL(href)
}

function ItemModal({ item, establishmentId, onClose, onSaved }: { item: Partial<Item>; establishmentId: string; onClose: () => void; onSaved: () => void }) {
  const isNew = !item.id
  const [f, setF] = useState({
    categorie: item.categorie || 'autre', libelle: item.libelle || '',
    dernierControle: item.dernierControle || '', periodiciteMois: item.periodiciteMois != null ? String(item.periodiciteMois) : '',
    prochaineEcheance: item.prochaineEcheance || '', prestataire: item.prestataire || '', documentUrl: item.documentUrl || '', notes: item.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))

  async function save() {
    setSaving(true); setError('')
    try {
      const body: any = { ...f, establishmentId }
      // Dernier contrôle modifié sans nouvelle échéance saisie → le serveur recalcule l'échéance
      if (!isNew && f.dernierControle !== (item.dernierControle || '') && f.prochaineEcheance === (item.prochaineEcheance || '')) delete body.prochaineEcheance
      if (isNew) await api.post('/qse/compliance', body)
      else await api.put(`/qse/compliance/${item.id}`, body)
      onSaved()
    } catch (e: any) { setError(e?.response?.data?.error || 'Enregistrement impossible') } finally { setSaving(false) }
  }

  async function archive() {
    if (!confirm('Retirer cette échéance de la liste ? (elle reste en base, rien n\'est effacé)')) return
    await api.put(`/qse/compliance/${item.id}`, { actif: false })
    onSaved()
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900">{isNew ? 'Nouvelle échéance' : 'Modifier l\'échéance'}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Catégorie</label>
              <select className={inp} value={f.categorie} onChange={e => set('categorie', e.target.value)}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></div>
            <div><label className={lbl}>Prestataire</label><input className={inp} value={f.prestataire} onChange={e => set('prestataire', e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Libellé</label><input className={inp} value={f.libelle} onChange={e => set('libelle', e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Dernier contrôle</label><input type="date" className={inp} value={f.dernierControle} onChange={e => set('dernierControle', e.target.value)} /></div>
            <div><label className={lbl}>Tous les (mois)</label><input type="number" min={1} className={inp} value={f.periodiciteMois} onChange={e => set('periodiciteMois', e.target.value)} /></div>
            <div><label className={lbl}>Prochaine</label><input type="date" className={inp} value={f.prochaineEcheance} onChange={e => set('prochaineEcheance', e.target.value)} /></div>
          </div>
          <p className="text-xs text-gray-500">Si la prochaine date est vide, elle est calculée : dernier contrôle + périodicité.</p>
          <div><label className={lbl}>Lien du rapport (Drive…)</label><input className={inp} value={f.documentUrl} onChange={e => set('documentUrl', e.target.value)} placeholder="https://" /></div>
          <div><label className={lbl}>Notes</label><textarea className={inp} rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} /></div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t">
          {!isNew ? <button onClick={archive} className="text-sm text-gray-500 hover:text-red-600">Retirer de la liste</button> : <span />}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}

function SettingsPanel({ establishmentId, canEdit }: { establishmentId: string; canEdit: boolean }) {
  const [s, setS] = useState<Settings | null>(null)
  const [fournisseurs, setFournisseurs] = useState('')
  const [lieux, setLieux] = useState('')
  const [msg, setMsg] = useState('')
  useEffect(() => {
    api.get(`/qse/settings?establishmentId=${establishmentId}`).then(r => { setS(r.data); setFournisseurs((r.data.fournisseurs || []).join(', ')); setLieux((r.data.lieux || []).join(', ')) }).catch(() => {})
  }, [establishmentId])
  if (!s) return null
  async function save() {
    setMsg('')
    try {
      const r = await api.put('/qse/settings', {
        ...s, establishmentId,
        fournisseurs: fournisseurs.split(',').map(x => x.trim()).filter(Boolean),
        lieux: lieux.split(',').map(x => x.trim()).filter(Boolean),
        livraisons: (s!.livraisons || []).filter(p => p.jours.length && ((p.du && p.au) || (p.du === undefined && p.au === undefined))),
      })
      setS(r.data); setMsg('Enregistré')
    } catch (e: any) { setMsg(e?.response?.data?.error || 'Enregistrement impossible') }
  }
  return (
    <div className="bg-white rounded-xl border p-5 space-y-3">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Settings2 size={16} /> Réglages QSE du magasin</h2>
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2"><label className={lbl}>Mail du responsable QSE (alertes)</label>
          <input className={inp} disabled={!canEdit} value={s.responsableEmail || ''} onChange={e => setS({ ...s, responsableEmail: e.target.value })} placeholder="alertes d'échéances à 8 h" /></div>
        <div><label className={lbl}>Livraison frais max (°C)</label>
          <input type="number" className={inp} disabled={!canEdit} value={s.seuilFrais} onChange={e => setS({ ...s, seuilFrais: Number(e.target.value) })} /></div>
        <div><label className={lbl}>Livraison surgelé max (°C)</label>
          <input type="number" className={inp} disabled={!canEdit} value={s.seuilSurgele} onChange={e => setS({ ...s, seuilSurgele: Number(e.target.value) })} /></div>
        <div><label className={lbl}>Écart écran / sonde max (°C)</label>
          <input type="number" step="0.5" className={inp} disabled={!canEdit} value={s.ecartMaxSonde} onChange={e => setS({ ...s, ecartMaxSonde: Number(e.target.value) })} /></div>
        <div className="md:col-span-3"><label className={lbl}>Fournisseurs proposés (séparés par des virgules)</label>
          <input className={inp} disabled={!canEdit} value={fournisseurs} onChange={e => setFournisseurs(e.target.value)} /></div>
        <div className="md:col-span-4"><label className={lbl}>Jours de livraison (tâches « veille de livraison », « pas le matin d'une livraison »)</label>
          <LivraisonsEditor value={s.livraisons || []} onChange={v => setS({ ...s, livraisons: v })} canEdit={canEdit} /></div>
        <div className="md:col-span-4"><label className={lbl}>Lieux en plus pour les signalements (séparés par des virgules ; les zones des tâches sont déjà proposées)</label>
          <input className={inp} disabled={!canEdit} value={lieux} onChange={e => setLieux(e.target.value)} /></div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-3">
          <button onClick={save} className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg"><Save size={14} /> Enregistrer</button>
          {msg && <span className="text-sm text-gray-600">{msg}</span>}
        </div>
      )}
    </div>
  )
}

// Écran « Conformité QSE » : échéancier (vert / orange / rouge), registre du mois pour l'auditeur, réglages.
export default function Conformite() {
  const { current } = useEstablishment()
  const estId = current?.id
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const isAdmin = ['super_admin', 'admin'].includes(user.role)
  const [items, setItems] = useState<Item[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState<Partial<Item> | null>(null)
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState('')

  const load = useCallback(async () => {
    if (!estId) return
    setLoading(true)
    try {
      const [a, b] = await Promise.all([
        api.get(`/qse/compliance?establishmentId=${estId}`),
        api.get(`/qse/summary?establishmentId=${estId}`),
      ])
      setItems(a.data); setSummary(b.data)
    } catch { } finally { setLoading(false) }
  }, [estId])
  useEffect(() => { load() }, [load])

  async function seed() {
    setMsg('')
    const r = await api.post('/qse/seed/compliance', { establishmentId: estId })
    setMsg(r.data.created ? `${r.data.created} échéance(s) importée(s)` : 'Déjà importé, rien de nouveau')
    load()
  }

  async function registre(format: 'pdf' | 'xlsx') {
    if (!estId || !current) return
    setBusy(format)
    try { await download(`/qse/register?establishmentId=${estId}&month=${month}&format=${format}`, `Registre_QSE_${current.name.replace(/\s+/g, '_')}_${month}.${format}`) }
    catch { setMsg('Registre impossible à générer') } finally { setBusy('') }
  }

  const cards = summary ? [
    { label: 'Contrôles dépassés', value: summary.echeancesDepassees, color: summary.echeancesDepassees ? 'text-red-600' : 'text-gray-900' },
    { label: 'À prévoir sous 30 jours', value: summary.echeancesProches, color: summary.echeancesProches ? 'text-amber-600' : 'text-gray-900' },
    { label: 'Températures hors limite (7 j)', value: summary.temperaturesHorsLimite7j, color: summary.temperaturesHorsLimite7j ? 'text-red-600' : 'text-gray-900' },
    { label: 'Livraisons non conformes (7 j)', value: summary.livraisonsNonConformes7j, color: summary.livraisonsNonConformes7j ? 'text-red-600' : 'text-gray-900' },
  ] : []

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><ShieldCheck size={22} className="text-emerald-600" /> Conformité QSE</h1>
          <p className="text-sm text-gray-500">{current?.name} — contrôles obligatoires, registre pour l'auditeur</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100"><RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cards.map(c => (
            <div key={c.label} className="bg-white rounded-xl border p-4">
              <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
              <div className="text-xs text-gray-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><FileText size={16} /> Registre QSE du mois</h2>
          <p className="text-sm text-gray-500 mb-3">Températures, livraisons, tâches QSE, non-conformités et actions correctives, missions d'audit et échéancier : un seul fichier à montrer à l'auditeur.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => registre('pdf')} disabled={!!busy} className="flex items-center gap-2 bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
              <Download size={14} /> {busy === 'pdf' ? 'Génération...' : 'PDF'}
            </button>
            <button onClick={() => registre('xlsx')} disabled={!!busy} className="flex items-center gap-2 bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
              <Download size={14} /> {busy === 'xlsx' ? 'Génération...' : 'Excel'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border">
          <div className="flex items-center justify-between px-5 py-4 border-b flex-wrap gap-2">
            <h2 className="font-semibold text-gray-900">Échéancier de conformité</h2>
            <div className="flex items-center gap-2">
              {msg && <span className="text-sm text-gray-600">{msg}</span>}
              {isAdmin && (
                <button onClick={seed} className="flex items-center gap-2 border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium px-3 py-2 rounded-lg">
                  <Download size={14} /> Échéancier de départ
                </button>
              )}
              <button onClick={() => setEdit({})} className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-lg"><Plus size={14} /> Échéance</button>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <AlertTriangle size={28} className="mx-auto mb-2 text-gray-300" />
              Aucune échéance. {isAdmin ? 'Cliquer sur « Échéancier de départ » pour importer les contrôles connus du magasin.' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                    <th className="px-4 py-2">État</th><th className="px-4 py-2">Prochaine</th><th className="px-4 py-2">Contrôle</th>
                    <th className="px-4 py-2">Catégorie</th><th className="px-4 py-2">Dernier</th><th className="px-4 py-2">Prestataire</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(i => (
                    <tr key={i.id} onClick={() => setEdit(i)} className={`border-b cursor-pointer hover:brightness-95 ${ETAT[i.etat].row}`}>
                      <td className="px-4 py-2.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${ETAT[i.etat].pill}`}>{ETAT[i.etat].label}</span></td>
                      <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{frDate(i.prochaineEcheance)}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-900">{i.libelle}</div>
                        {i.notes && <div className="text-xs text-gray-500">{i.notes}</div>}
                        {i.documentUrl && <a href={i.documentUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-blue-600 underline">rapport</a>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{catLabel(i.categorie)}</td>
                      <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{frDate(i.dernierControle)}{i.periodiciteMois ? <span className="text-xs text-gray-400"> · {i.periodiciteMois} mois</span> : null}</td>
                      <td className="px-4 py-2.5 text-gray-600">{i.prestataire || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {estId && <SettingsPanel establishmentId={estId} canEdit={isAdmin} />}
      </div>

      {edit && estId && <ItemModal item={edit} establishmentId={estId} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load() }} />}
    </div>
  )
}
